import { useCallback, useRef } from 'react';
import { useBlocker, type ShouldBlockFn } from '@tanstack/react-router';

export type UnsavedChangesGuard = {
  /** A navigation is parked, waiting for the user to answer the prompt. */
  isBlocked: boolean;
  /** Cancel the parked navigation — the user keeps their work and the page. */
  stay: () => void;
  /** Release the parked navigation, abandoning the unsaved work. */
  leave: () => void;
  /**
   * Wave the NEXT navigation through without prompting, then re-arm. Call it
   * immediately before navigating away yourself once the work is persisted:
   * react-hook-form still reports `isDirty` right after a successful save (the
   * form is only reset on the next render, or not at all when the route
   * unmounts), so without this the app's own post-save redirect would trip the
   * guard it just satisfied.
   */
  allowNextNavigation: () => void;
};

/**
 * Warn before unsaved work is thrown away.
 *
 * Blocks in-app navigation through the router's own blocker (back button,
 * links, programmatic `navigate`) and mirrors it onto `beforeunload` so closing
 * or reloading the tab warns too. Pair it with `UnsavedChangesDialog`, which
 * renders the "stay or leave" choice:
 *
 * ```tsx
 * const guard = useUnsavedChangesGuard({ when: form.formState.isDirty && !isSaving });
 * // …after a successful save, before redirecting:
 * guard.allowNextNavigation();
 * // …at the end of the page:
 * <UnsavedChangesDialog open={guard.isBlocked} onStay={guard.stay} onLeave={guard.leave} />
 * ```
 *
 * `when` is the whole condition — "there is work to lose". Include the
 * in-flight save in it (`isDirty && !isSaving`), because a save is itself a
 * reason to be leaving.
 *
 * By default only a change of pathname prompts, so search-param updates the
 * page makes to itself (filters, tabs, an opened drawer) never nag. Pass
 * `shouldBlockFn` when a route needs a narrower rule.
 */
export function useUnsavedChangesGuard({
  when,
  shouldBlockFn,
}: {
  when: boolean;
  shouldBlockFn?: ShouldBlockFn;
}): UnsavedChangesGuard {
  const allowNextNavigationRef = useRef(false);

  const blocker = useBlocker({
    disabled: !when,
    enableBeforeUnload: when,
    withResolver: true,
    shouldBlockFn: (args) => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false;
        return false;
      }
      if (shouldBlockFn) return shouldBlockFn(args);
      return args.current.pathname !== args.next.pathname;
    },
  });

  const stay = useCallback(() => {
    if (blocker.status === 'blocked') blocker.reset();
  }, [blocker]);

  const leave = useCallback(() => {
    if (blocker.status === 'blocked') blocker.proceed();
  }, [blocker]);

  const allowNextNavigation = useCallback(() => {
    allowNextNavigationRef.current = true;
  }, []);

  return {
    isBlocked: blocker.status === 'blocked',
    stay,
    leave,
    allowNextNavigation,
  };
}
